import type {
  Bundle,
  CodeableConcept,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import {
  QUEUED_EDITS_TASK_IO_SYSTEM,
  QueuedEditsTaskIO,
} from '#src/codeSystems.ts'
import { TASK_TYPES } from '#src/models/tasks/constants.ts'
import {
  StandardTask,
  type StandardTaskParams,
} from '#src/models/tasks/standard/standardTask.ts'
import type { QueryTypes } from '#src/search.ts'

export interface QueuedEditsReviewTaskParams extends StandardTaskParams {
  participant: Reference<Patient>
  // Optional at construction: a task may be created as a draft first (to mint
  // its id before the proposal bundle is built) and have its proposal bundle
  // attached once the bundle is saved.
  proposedBundle?: Reference<Bundle>
}

// Async human-in-the-loop review of a proposed FHIR change Bundle (see
// docs/human-in-the-loop-queued-edits.md). Pure orchestration: it references the
// persisted proposal Bundle on `Task.focus` and carries the review lifecycle via
// `state`. `focus` (not `input`) so the link is searchable — `Task?focus=Bundle/<id>`
// answers "is this proposal already filed as a task?", which lets the inline
// chat tool turn its save-for-later affordance into a link to the existing task
// durably (across reloads, on any surface). The Bundle is the self-contained
// source of truth — the reviewer's per-entry decisions and the final committer
// live ON the Bundle (queued-edits/decisions.ts), not on Task.output. The
// participant scope rides `Task.for` / the `patient` search param. The Task
// exists only to make an async review discoverable in the inbox; the inline
// ai-chat case creates none until the user promotes it.
export class QueuedEditsReviewTask extends StandardTask {
  static defaultTitle = 'Review proposed changes'

  static get code(): CodeableConcept {
    return TASK_TYPES.QUEUED_EDITS_REVIEW
  }

  // Pending review tasks for a participant — the idempotency probe proposers
  // run before queueing a new one.
  static byParticipantPending(participant: Reference<Patient>): QueryTypes {
    return {
      code: QueuedEditsReviewTask.code,
      patient: participant.reference,
      'business-status': ['todo', 'in-progress', 'backlog'],
      'status:not': ['entered-in-error', 'completed', 'cancelled'],
    }
  }

  // Review tasks that reference a given proposal Bundle. Backed by the standard
  // `focus` search param, so the inline tool can resolve "has this Bundle been
  // promoted to a task?" from the Bundle reference alone.
  static byBundle(bundle: Reference<Bundle>): QueryTypes {
    return QueuedEditsReviewTask.byFocus(bundle)
  }

  constructor({
    participant,
    proposedBundle,
    ...rest
  }: QueuedEditsReviewTaskParams) {
    super({
      code: QueuedEditsReviewTask.code,
      for: participant,
      ...rest,
    })
    if (!this.title) {
      this.title = QueuedEditsReviewTask.defaultTitle
    }
    this.state = 'todo'
    this.proposedBundle = proposedBundle
  }

  // The proposal Bundle this review is for, stored on the searchable `Task.focus`.
  // Only returns focus when it actually references a Bundle: a draft has no
  // focus, and a task written before the input→focus move may carry a different
  // focus (the participant). Such a legacy task falls back to the bundle ref
  // recorded on `Task.input`, so it still resolves until re-saved.
  get proposedBundle(): Reference<Bundle> | undefined {
    const focus = this.focus
    if (focus?.reference?.startsWith('Bundle/')) {
      return focus as Reference<Bundle>
    }
    return this.legacyInputBundle
  }

  set proposedBundle(bundle: Reference<Bundle> | undefined) {
    this.focus = bundle
  }

  // Legacy location: review tasks created before the input→focus move carried
  // the proposal Bundle on a typed `Task.input` entry.
  private get legacyInputBundle(): Reference<Bundle> | undefined {
    return this.resource.input?.find(
      (input) =>
        input.type?.coding?.[0]?.system === QUEUED_EDITS_TASK_IO_SYSTEM &&
        input.type.coding[0].code === QueuedEditsTaskIO.PROPOSED_BUNDLE
    )?.valueReference as Reference<Bundle> | undefined
  }
}
